import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { LoadingSpinner } from '@atrvd/saas-ui'
import { useSetBreadcrumb } from '@/components/layout/BreadcrumbContext'
import { SAME_SCREEN_NAVIGATION } from '@/hooks/useDismissErrorToastsOnNavigate'
import { useFacilityScope } from '@/hooks/useFacilityScope'
import { FacilityScopeFallback } from '@/components/financial-accounts/FacilityScopeFallback'
import { FinancialAccountsPageHeader } from '@/components/financial-accounts/FinancialAccountsPageHeader'
import {
  financialAccountsBasePath,
  type FacilityFinancialAccountsScope,
} from '@/components/financial-accounts/financialAccountsScope'
import { useFacilityScopeBreadcrumb } from '@/components/financial-accounts/useFacilityScopeBreadcrumb'
import { ManualBankDetailHeader } from '@/components/manual-banks/ManualBankDetailHeader'
import { ManualBankAccountsTable } from '@/components/manual-banks/ManualBankAccountsTable'
import { MANUAL_BANK_COPY } from '@/components/manual-banks/manualBankCopy'
import { manualBankPath, type ManualBankInScope } from '@/components/manual-banks/manualBanks.utils'
import { useManualBanksInScope } from '@/components/manual-banks/useManualBanksInScope'

/**
 * One manual bank and its accounts or cards.
 *
 * Routes, below the organization-wide page (`/settings/financial-accounts`)
 * or a facility's (`/settings/facilities/:facilityId/financial-accounts`):
 * `…/manual/:institutionName`, the name URL-encoded (react-router hands the
 * param back decoded), and `…/manual-unnamed` — no param — for the accounts
 * without a name. A manual bank has no id, so its name is its address;
 * renaming it therefore moves the page.
 *
 * On a facility's route the page shows only that facility's accounts of the
 * bank and is read-only for an inactive facility.
 */
export function ManualBankPage() {
  const { scope, scoped, isLoading, error, notFound } = useFacilityScope()

  if (!scoped) return <ManualBankView scope={null} />
  if (scope) return <ManualBankView scope={scope} />
  return <FacilityScopeFallback isLoading={isLoading} error={error} notFound={notFound} />
}

interface ManualBankViewProps {
  scope: FacilityFinancialAccountsScope | null
}

/** The page once its scope is known. */
function ManualBankView({ scope }: ManualBankViewProps) {
  const { institutionName: param } = useParams<{ institutionName: string }>()
  const institutionName = param ?? null
  const navigate = useNavigate()
  const basePath = financialAccountsBasePath(scope)
  const { findBank, isLoading: loading, isFetching, error } = useManualBanksInScope(scope)
  // This may list none of the bank's accounts while the bank still has
  // accounts elsewhere — at another facility, or an inactive one (`bankAccountIds`).
  const live = useMemo(() => findBank(institutionName), [findBank, institutionName])
  /**
   * The bank as it was when its last listed account was deleted (or, on a
   * facility's page, moved to another facility) here, or when it was saved
   * from here without a rename — the edit may have moved them all away. A
   * manual bank IS its accounts, so without one it vanishes from the list; the
   * page keeps showing it (header, empty table, "Add account or card") until
   * the user leaves, so adding an account brings it back. While the bank still has
   * accounts the page does not list, the live data stands in for this snapshot
   * so the bank-wide Edit and Delete reach them. A fresh load of the URL finds
   * nothing and says so.
   */
  const [emptied, setEmptied] = useState<ManualBankInScope | null>(null)
  const bank =
    live && live.accounts.length > 0
      ? live
      : emptied && emptied.institutionName === institutionName
        ? (live ?? emptied)
        : null

  // Keyed by the decoded name, which is how Breadcrumbs looks the segment up.
  // The unnamed route's crumb is a static route label instead.
  useSetBreadcrumb(param, bank?.displayName)
  useFacilityScopeBreadcrumb(scope)

  return (
    <div className="space-y-6">
      <FinancialAccountsPageHeader scope={scope} backToList />

      {/* A refetch in flight counts as loading when the bank is missing: right
          after a rename this page is at the new name while the cached list
          still holds the old one. */}
      {loading || (!bank && isFetching) ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : error && !bank ? (
        // A failed background refetch keeps the cached bank; only a load that
        // never produced it replaces the page.
        <div className="py-12 text-center">
          <p role="alert" className="text-destructive">
            {error.message}
          </p>
        </div>
      ) : !bank ? (
        <div className="py-12 text-center">
          <p className="text-muted-foreground">{MANUAL_BANK_COPY.notFound}</p>
        </div>
      ) : (
        <>
          <ManualBankDetailHeader
            bank={bank}
            readOnly={scope?.readOnly ?? false}
            onSaved={(name) => {
              if (name === bank.institutionName) {
                // The edit may have moved every account listed here to another
                // facility; like the last-account path below, the emptied bank
                // stays on screen rather than turning into "not found".
                setEmptied({ ...bank, accounts: [], bankAccountIds: [] })
              } else {
                // Same screen, new address: a partial-failure toast the save
                // just raised must survive this move (it is swept on the next).
                navigate(manualBankPath(basePath, { institutionName: name }), {
                  replace: true,
                  state: SAME_SCREEN_NAVIGATION,
                })
              }
            }}
            onDeleted={() => navigate(basePath, { replace: true })}
          />
          <ManualBankAccountsTable
            key={manualBankPath(basePath, bank)}
            bank={bank}
            scope={scope}
            onLastAccountRemoved={() => setEmptied({ ...bank, accounts: [], bankAccountIds: [] })}
          />
        </>
      )}
    </div>
  )
}
